import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Badge, Button, Container, Form, Spinner, Table } from 'react-bootstrap';
import { ArrowClockwise, Link45deg, ShieldFillCheck, ShieldSlash } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import EMMSectionHeader from './EMMSectionHeader';
import {
  useGetDomainsQuery,
  useGetEmailsQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
} from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../hooks/useTenantIdForPreferences';

type BlocklistEntry = {
  id?: string;
  value?: string;
  type?: 'email' | 'domain' | string;
  addedAt?: string;
  source?: string;
};

type BounceType = 'hard' | 'soft' | 'unknown';

type BounceRecipientRow = {
  recipient: string;
  bounceCount: number;
  bounceType: BounceType;
  bounceReason: string;
  isRetryable: boolean;
  lastBouncedAt: string;
  lastSubject: string;
  lastMailbox: string;
  blockedBy: 'email' | 'domain' | null;
  blockSource?: string | null;
};

const SYSTEM_BOUNCE_LOCAL_PARTS = new Set([
  'abuse',
  'abuse-pro',
  'postmaster',
  'mailer-daemon',
  'mailerdaemon',
  'no-reply',
  'noreply',
]);
const SYSTEM_BOUNCE_DOMAIN_HINTS = [
  'smtp.com',
  'mailchannels.net',
  'mailchannels.com',
  'amazonses.com',
  'amazonaws.com',
  'sendgrid.net',
  'sgmail.com',
];

function genId(): string {
  return `bounce-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeEmailAddress(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getDomainFromEmail(email: string): string {
  const normalized = normalizeEmailAddress(email);
  const parts = normalized.split('@');
  return parts.length > 1 ? parts[1] : '';
}

function normalizeDomainValue(value: unknown): string {
  return String(value || '').trim().toLowerCase().replace(/^@+/, '').replace(/\.+$/, '');
}

function isLikelySystemBounceAddress(value: unknown): boolean {
  const normalized = normalizeEmailAddress(value);
  if (!isValidEmail(normalized)) return true;
  const [localPart, domain] = normalized.split('@');
  if (!localPart || !domain) return true;
  if (SYSTEM_BOUNCE_LOCAL_PARTS.has(localPart)) return true;
  if (
    /^(abuse|postmaster|mailer-daemon|mailerdaemon|no-reply|noreply)([+._-]|$)/.test(localPart) ||
    /^(test|qa|staging|sandbox|demo)([+._-]|$)/.test(localPart)
  ) {
    return true;
  }
  if (domain === 'localhost' || domain === 'invalid' || domain.endsWith('.invalid')) return true;
  return SYSTEM_BOUNCE_DOMAIN_HINTS.some((hint) => domain === hint || domain.endsWith(`.${hint}`));
}

function parseIsoMs(value: unknown): number {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value: string): string {
  const parsedMs = parseIsoMs(value);
  if (!parsedMs) return '—';
  return new Date(parsedMs).toLocaleString();
}

function normalizeBounceType(value: unknown): BounceType {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'hard' || normalized === 'soft' || normalized === 'unknown') {
    return normalized;
  }
  return 'unknown';
}

function normalizeBounceReason(value: unknown, type: BounceType): string {
  const reason = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .slice(0, 120);
  if (reason) return reason;
  if (type === 'hard') return 'permanent failure';
  if (type === 'soft') return 'temporary failure';
  return 'unclassified';
}

function getBlockSourceLabel(source: unknown): string {
  const normalized = String(source || '').trim().toLowerCase();
  if (normalized === 'system_hard_bounce') return 'Automatic';
  if (normalized === 'bounced-list') return 'Manual';
  if (normalized === 'csv-import') return 'CSV';
  if (normalized === 'manual') return 'Manual';
  if (!normalized) return 'Manual';
  return normalized.replace(/[_-]+/g, ' ');
}

function extractEmailsArray(payload: any): any[] {
  if (Array.isArray(payload?.data?.emails)) return payload.data.emails;
  if (Array.isArray(payload?.emails)) return payload.emails;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function extractBounceRecipients(email: any): string[] {
  const backendRecipients: string[] = Array.isArray(email?.bounceRecipients)
    ? (email.bounceRecipients as unknown[])
        .map((entry: unknown) => normalizeEmailAddress(entry))
        .filter((entry) => isValidEmail(entry) && !entry.includes('example.com') && !isLikelySystemBounceAddress(entry))
    : [];
  if (backendRecipients.length > 0) {
    return Array.from(new Set(backendRecipients)).slice(0, 20);
  }

  const source = `${String(email?.subject || '')}\n${String(email?.text || '')}\n${String(email?.html || '')}`;
  if (!source.trim()) return [];

  const found = new Set<string>();
  const add = (candidate: unknown) => {
    const normalized = normalizeEmailAddress(candidate);
    if (!isValidEmail(normalized)) return;
    if (normalized.includes('example.com')) return;
    if (isLikelySystemBounceAddress(normalized)) return;
    found.add(normalized);
  };

  const explicitPatterns = [
    /Final-Recipient:\s*rfc822;\s*([^\s\n\r;<>]+)/gi,
    /Original-Recipient:\s*rfc822;\s*([^\s\n\r;<>]+)/gi,
    /delivering your message to\s+([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi,
    /message to\s+([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi,
    /recipient(?:s)?\s*[:=]\s*<?([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})>?/gi,
  ];

  explicitPatterns.forEach((regex) => {
    let match: RegExpExecArray | null = regex.exec(source);
    while (match) {
      add(match[1]);
      match = regex.exec(source);
    }
  });

  return Array.from(found).slice(0, 20);
}

export default function BouncedList() {
  const navigate = useNavigate();
  const accountContext = useAccount();
  const tenantId = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();

  const [searchTerm, setSearchTerm] = useState('');
  const [bounceTypeFilter, setBounceTypeFilter] = useState<'all' | 'hard' | 'soft'>('all');
  const [feedback, setFeedback] = useState<string>('');

  const {
    data: emailsData,
    isLoading: isLoadingEmails,
    isFetching: isRefreshingEmails,
    error: emailsError,
    refetch: refetchBounces,
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'Bounce',
      limit: 2000,
      ...preferencesTenantWriteSpread(tenantId),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: domainsData } = useGetDomainsQuery(undefined);

  const { data: prefsData } = useGetUserPreferencesQuery(userPreferencesQueryArg, {
    refetchOnMountOrArgChange: true,
  });
  const [updatePrefs, { isLoading: isSavingBlocklist }] = useUpdateUserPreferencesMutation();

  const preferences: any = (prefsData as any)?.preferences || {};
  const blocklistEntries: BlocklistEntry[] = useMemo(() => {
    const raw = preferences.blocklist;
    return Array.isArray(raw) ? raw : [];
  }, [preferences.blocklist]);

  const blockedEmails = useMemo(() => {
    const out = new Map<string, string>();
    blocklistEntries.forEach((entry) => {
      const type = String(entry?.type || '').toLowerCase();
      const value = normalizeEmailAddress(entry?.value);
      if (!value || !isValidEmail(value)) return;
      if (type === 'domain') return;
      out.set(value, String(entry?.source || '').trim().toLowerCase());
    });
    return out;
  }, [blocklistEntries]);

  const blockedDomains = useMemo(() => {
    const out = new Set<string>();
    blocklistEntries.forEach((entry) => {
      const type = String(entry?.type || '').toLowerCase();
      const value = normalizeEmailAddress(entry?.value);
      if (!value || type !== 'domain') return;
      out.add(value);
    });
    return out;
  }, [blocklistEntries]);

  const bouncedMessages = useMemo(() => extractEmailsArray(emailsData), [emailsData]);
  const observedMailboxDomains = useMemo(() => {
    const out = new Set<string>();
    bouncedMessages.forEach((message: any) => {
      const mailbox = normalizeEmailAddress(message?.emailAccount);
      if (!isValidEmail(mailbox)) return;
      const domain = getDomainFromEmail(mailbox);
      if (domain) out.add(domain);
    });
    return out;
  }, [bouncedMessages]);

  const protectedTenantDomains = useMemo(() => {
    const tenantKey = tenantId ? String(tenantId) : '';
    const out = new Set<string>();

    const accountRows = Array.isArray(accountContext?.accounts) ? accountContext.accounts : [];
    accountRows.forEach((account: any) => {
      const accountId = String(account?.id || account?.accountId || '').trim();
      if (tenantKey && accountId && accountId !== tenantKey) return;
      const accountDomains = Array.isArray(account?.domains) ? account.domains : [];
      accountDomains.forEach((domain: unknown) => {
        const normalized = normalizeDomainValue(domain);
        if (normalized) out.add(normalized);
      });
    });

    const apiDomains = Array.isArray((domainsData as any)?.data?.domains)
      ? (domainsData as any).data.domains
      : [];
    apiDomains.forEach((domainRow: any) => {
      const domainName = normalizeDomainValue(domainRow?.domain || domainRow?.id);
      if (!domainName) return;
      const domainTenant = String(domainRow?.accountId || domainRow?.tenantId || '').trim();
      if (tenantKey && domainTenant && domainTenant !== tenantKey) return;
      out.add(domainName);
    });

    return out;
  }, [tenantId, accountContext?.accounts, domainsData]);

  const rows = useMemo(() => {
    const byRecipient = new Map<string, Omit<BounceRecipientRow, 'blockedBy'> & { lastBouncedAtMs: number }>();

    bouncedMessages.forEach((message: any) => {
      const recipients = extractBounceRecipients(message);
      if (recipients.length === 0) return;

      const bouncedAt = String(message?.date || message?.createdAt || message?.updatedAt || '').trim();
      const bouncedAtMs = parseIsoMs(bouncedAt);
      const subject = String(message?.subject || '').trim() || '(No Subject)';
      const mailbox = String(message?.emailAccount || '').trim();
      const bounceType = normalizeBounceType(message?.bounceType);
      const bounceReason = normalizeBounceReason(message?.bounceReason, bounceType);
      const isRetryable = typeof message?.isRetryable === 'boolean'
        ? message.isRetryable
        : bounceType !== 'hard';

      recipients.forEach((recipient) => {
        const current = byRecipient.get(recipient);
        if (!current) {
          byRecipient.set(recipient, {
            recipient,
            bounceCount: 1,
            bounceType,
            bounceReason,
            isRetryable,
            lastBouncedAt: bouncedAt,
            lastBouncedAtMs: bouncedAtMs,
            lastSubject: subject,
            lastMailbox: mailbox,
          });
          return;
        }

        const nextCount = current.bounceCount + 1;
        if (bouncedAtMs >= current.lastBouncedAtMs) {
          byRecipient.set(recipient, {
            ...current,
            bounceCount: nextCount,
            bounceType,
            bounceReason,
            isRetryable,
            lastBouncedAt: bouncedAt || current.lastBouncedAt,
            lastBouncedAtMs: bouncedAtMs || current.lastBouncedAtMs,
            lastSubject: subject,
            lastMailbox: mailbox || current.lastMailbox,
          });
        } else {
          byRecipient.set(recipient, { ...current, bounceCount: nextCount });
        }
      });
    });

    return Array.from(byRecipient.values())
      .map((row) => {
        const recipientDomain = getDomainFromEmail(row.recipient);
        const mailboxDomain = getDomainFromEmail(row.lastMailbox);
        const isInternalRecipientDomain = protectedTenantDomains.has(recipientDomain);
        const isMailboxDomainMatch = Boolean(recipientDomain && mailboxDomain && recipientDomain === mailboxDomain);
        const isObservedMailboxDomain = observedMailboxDomains.has(recipientDomain);
        if (isInternalRecipientDomain || isMailboxDomainMatch || isObservedMailboxDomain) return null;
        const blockSource = blockedEmails.get(row.recipient) || null;
        const blockedBy = blockSource
          ? 'email'
          : blockedDomains.has(recipientDomain)
            ? 'domain'
            : null;
        return {
          recipient: row.recipient,
          bounceCount: row.bounceCount,
          bounceType: row.bounceType,
          bounceReason: row.bounceReason,
          isRetryable: row.isRetryable,
          lastBouncedAt: row.lastBouncedAt,
          lastSubject: row.lastSubject,
          lastMailbox: row.lastMailbox,
          blockedBy,
          blockSource,
        } as BounceRecipientRow;
      })
      .filter((row): row is BounceRecipientRow => Boolean(row))
      .sort((a, b) => parseIsoMs(b.lastBouncedAt) - parseIsoMs(a.lastBouncedAt));
  }, [bouncedMessages, blockedDomains, blockedEmails, protectedTenantDomains, observedMailboxDomains]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term && bounceTypeFilter === 'all') return rows;
    return rows.filter((row) => (
      (bounceTypeFilter === 'all' || row.bounceType === bounceTypeFilter) && (
        !term ||
        row.recipient.toLowerCase().includes(term) ||
        row.bounceType.toLowerCase().includes(term) ||
        row.bounceReason.toLowerCase().includes(term) ||
        row.lastSubject.toLowerCase().includes(term) ||
        row.lastMailbox.toLowerCase().includes(term)
      )
    ));
  }, [rows, searchTerm, bounceTypeFilter]);

  const bounceTypeSummary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc[row.bounceType] += 1;
        return acc;
      },
      { hard: 0, soft: 0, unknown: 0 } as Record<BounceType, number>
    );
  }, [rows]);

  const addEmailToBlocklist = useCallback(async (recipient: string) => {
    const normalized = normalizeEmailAddress(recipient);
    if (!isValidEmail(normalized)) return;
    if (blockedEmails.has(normalized)) return;

    const nextEntries = [
      {
        id: genId(),
        value: normalized,
        type: 'email',
        addedAt: new Date().toISOString(),
        source: 'bounced-list',
      },
      ...blocklistEntries,
    ];

    setFeedback('');
    try {
      await updatePrefs({
        ...preferencesTenantWriteSpread(tenantId),
        preferences: { blocklist: nextEntries },
      }).unwrap();
      setFeedback(`Added ${normalized} to Block List manually.`);
    } catch (error: any) {
      setFeedback(error?.data?.message || error?.message || `Failed to add ${normalized} to Block List.`);
    }
  }, [blocklistEntries, blockedEmails, tenantId, updatePrefs]);

  if (isLoadingEmails) {
    return (
      <>
        <EMMSectionHeader title="Bounced List" showHelp />
        <div className="d-flex justify-content-center align-items-center p-5">
          <Spinner animation="border" />
        </div>
      </>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="Bounced List"
        showHelp
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
      />
      <Container fluid className="pt-0 p-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="text-muted small">
            {rows.length} bounced recipient{rows.length !== 1 ? 's' : ''} from {bouncedMessages.length} Bounce-folder message{bouncedMessages.length !== 1 ? 's' : ''}.
            {' '}
            <span className="ms-1">
              Hard: {bounceTypeSummary.hard} | Soft: {bounceTypeSummary.soft} | Unknown: {bounceTypeSummary.unknown}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-1">
              <span className="text-muted small">Type:</span>
              <Form.Select
                size="sm"
                value={bounceTypeFilter}
                onChange={(event) => setBounceTypeFilter(event.target.value as 'all' | 'hard' | 'soft')}
                style={{ fontSize: '0.72rem', paddingTop: '0.15rem', paddingBottom: '0.15rem', minWidth: 108 }}
                aria-label="Filter bounced recipients by type"
              >
                <option value="all">All</option>
                <option value="hard">Hard</option>
                <option value="soft">Soft</option>
              </Form.Select>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', lineHeight: 1.2 }}
              onClick={() => navigate('/unibox')}
            >
              <Link45deg size={10} className="me-1" />
              Open Unibox
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', lineHeight: 1.2 }}
              onClick={() => refetchBounces()}
              disabled={isRefreshingEmails}
            >
              <ArrowClockwise size={10} className="me-1" />
              {isRefreshingEmails ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
        <div className="text-muted small mb-3">
          Block List Status shows current Block List membership only. It does not mean hard-bounce automation is active for every tenant.
        </div>

        {feedback ? <Alert variant="info" className="py-2">{feedback}</Alert> : null}

        {emailsError ? (
          <Alert variant="danger" className="py-2">
            Failed to load Bounce folder data.
          </Alert>
        ) : null}

        {filteredRows.length === 0 ? (
          <Alert variant="light" className="text-center py-4">
            {rows.length === 0
              ? 'No bounced recipients found yet.'
              : 'No bounced recipients match your current filters.'}
          </Alert>
        ) : (
          <Table hover responsive size="sm" className="align-middle">
            <thead>
              <tr style={{ fontSize: '0.8rem' }}>
                <th>Recipient</th>
                <th style={{ width: 90 }}>Bounces</th>
                <th style={{ width: 180 }}>Bounce Type</th>
                <th style={{ width: 220 }}>Last Bounced</th>
                <th>Latest Bounce Subject</th>
                <th style={{ width: 220 }}>Mailbox</th>
                <th style={{ width: 160 }}>Block Status</th>
                <th style={{ width: 140 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.recipient}
                  className={row.bounceType === 'hard' ? 'table-danger' : undefined}
                  style={{ fontSize: '0.82rem' }}
                >
                  <td className="fw-semibold">{row.recipient}</td>
                  <td>{row.bounceCount}</td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      {row.bounceType === 'hard' ? (
                        <Badge bg="danger" style={{ fontSize: '0.65rem', width: 'fit-content' }}>
                          Hard
                        </Badge>
                      ) : row.bounceType === 'soft' ? (
                        <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem', width: 'fit-content' }}>
                          Soft
                        </Badge>
                      ) : (
                        <Badge bg="secondary" style={{ fontSize: '0.65rem', width: 'fit-content' }}>
                          Unknown
                        </Badge>
                      )}
                      <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {row.bounceReason}
                        {row.isRetryable ? ' (retryable)' : ' (non-retryable)'}
                      </span>
                    </div>
                  </td>
                  <td className="text-muted">{formatDate(row.lastBouncedAt)}</td>
                  <td className="text-muted">{row.lastSubject}</td>
                  <td className="text-muted">{row.lastMailbox || '—'}</td>
                  <td>
                    {row.blockedBy === 'email' ? (
                      <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                        <ShieldFillCheck size={10} className="me-1" />
                        Blocked ({getBlockSourceLabel(row.blockSource)})
                      </Badge>
                    ) : row.blockedBy === 'domain' ? (
                      <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                        Blocked (domain rule)
                      </Badge>
                    ) : (
                      <Badge bg="light" text="dark" style={{ fontSize: '0.65rem', border: '1px solid #dee2e6' }}>
                        Not in Block List
                      </Badge>
                    )}
                  </td>
                  <td>
                    {row.blockedBy === 'email' ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{ fontSize: '0.72rem' }}
                        onClick={() => navigate('/block-list')}
                      >
                        Manage
                      </Button>
                    ) : row.blockedBy === 'domain' ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{ fontSize: '0.72rem' }}
                        onClick={() => navigate('/block-list')}
                      >
                        Domain rule
                      </Button>
                    ) : (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        style={{ fontSize: '0.68rem', padding: '0.12rem 0.38rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}
                        onClick={() => void addEmailToBlocklist(row.recipient)}
                        disabled={isSavingBlocklist}
                      >
                        <ShieldSlash size={10} className="me-1" />
                        Block
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Container>
    </>
  );
}
